// src/pages/DeliveryStatus/utils/fetch.js
import { message } from "antd";
import config from "../../../config.json";
import { postRequest, patchRequest, getRequest } from "../../../utils/requests";

/**
 * Загрузить страницы заявок доставки с бэка, отфильтровать по isHasDelivery
 * и записать в setSpecifications + setTotalCount (оригинальный totalCount).
 */
export async function fetchStatuses(
  state = "NEW",
  status = "",
  query = "",
  setSpecifications = () => {},
  setIsLoading = () => {},
  setTotalCount = () => {},
  page = 1,
  size = 20
) {
  setIsLoading(true);
  try {
    const url = `${config.service}/api/appl/state?search=${encodeURIComponent(query)}`;
    const body = { state, page, size };
    if (status) body.status = status;

    const response = await postRequest(url, body);
    const rawApps = Array.isArray(response.data.application)
      ? response.data.application
      : [];
    const deliveryApps = rawApps.filter((app) => app.isHasDelivery);

    setSpecifications(deliveryApps);
    setTotalCount(response.data.totalCount ?? 0);

    return {
      application: deliveryApps,
      totalCount: response.data.totalCount ?? 0,
      currentPage: response.data.currentPage ?? page,
    };
  } catch (err) {
    const msg = err?.response?.data?.message || "Неизвестная ошибка";
    message.error(msg);
    throw err;
  } finally {
    setIsLoading(false);
  }
}

/**
 * Подгрузить позиции заказа (без сервисных/монтажных/доставочных).
 */
export async function fetchSpecificationOrders(
  specificationId,
  setOrders = () => {},
  setIsLoading = () => {}
) {
  setIsLoading(true);
  try {
    const url = `${config.service}/api/appl/orders/${specificationId}`;
    const response = await getRequest(url);
    const products = Array.isArray(response.data.products)
      ? response.data.products
      : [];

    const filtered = products.filter((item) => {
      const type = item.productType || item.product?.productType;
      const name = item.name || item.product?.name || "";
      return !(
        type === "6323409e1870a29049455ae4" || // монтаж
        type === "6323e9251870a29049456a74" || // услуга
        type === "63298b75d5c733d1dfd94c7a" || // индивидуальный просчет
        type === "66615dc792168d32c76e3809" || // доставка
        /сборка/i.test(name)
      );
    });

    setOrders(filtered);
  } catch {
    message.error("Ошибка загрузки позиций заказа");
  } finally {
    setIsLoading(false);
  }
}

/**
 * Принять документы для доставки.
 * @returns {Promise<void>}
 */
export async function acceptDocumentsForDelivery(id) {
  const url = `${config.service}/api/appl/changeState/${id}`;
  const hide = message.loading("Загрузка...", 0);
  try {
    await patchRequest(url, { state: "ACTIVE", status: "ON_DELIVERY" });
    message.success("Документы успешно приняты для доставки.", 3);
  } catch (err) {
    message.error("Ошибка при принятии документов.", 3);
    throw err;
  } finally {
    hide();
  }
}

/**
 * Отправить клиенту код доставки.
 * @returns {Promise<void>}
 */
export async function sendDeliveryCodeToClient(id, phone) {
  const url = `${config.service}/api/appl/verificationCode/${id}`;
  try {
    const response = await postRequest(url, {
      phone,
      type: "DELIVERY",
    });
    if (response.status === 200) {
      message.success("Код доставки отправлен.", 3);
      return;
    }
    throw new Error("Не удалось отправить код");
  } catch (err) {
    message.error(err?.response?.data?.message || "Ошибка отправки кода", 3);
    throw err;
  }
}

/**
 * Завершить доставку по PIN-коду.
 * @returns {Promise<void>}
 */
export async function completeDelivery(pin, lead) {
  const url = `${config.service}/api/appl/verifyDeliveryCode/${lead._id}`;
  try {
    const response = await postRequest(url, {
      id: lead._id,
      code: pin,
      type: "DELIVERY",
      isHasMontage: lead.isHasMontage,
      phone: lead.client[0].phone,
    });
    if (response.status === 200) {
      return;
    }
    throw new Error("Неверный код");
  } catch (err) {
    message.error(err.message || "Ошибка проверки кода", 3);
    throw err;
  }
}

/**
 * Принудительно завершить доставку.
 * @returns {Promise<void>}
 */
export async function forceCompleteDelivery(id) {
  const url = `${config.service}/api/appl/force-complete/${id}`;
  try {
    const response = await postRequest(url, {});
    if (response.status === 200) {
      message.success("Доставка принудительно завершена.", 3);
      return;
    }
    throw new Error("Не удалось принудительно завершить");
  } catch (err) {
    message.error(err?.response?.data?.message || "Ошибка принудительного завершения", 3);
    throw err;
  }
}
